import axios from "axios";

/**
 * SMEConnext SSO — Source flow Vuex module
 *
 * - destinations: cached list of e-services the user can navigate to
 * - generateToken: requests one-time token from API and returns the
 *   redirectUrl (UI navigates with window.location.href)
 *
 * Destination flow (verify-token / SsoLanding) lands in Phase 2.
 */

export const state = {
  destinations: [],
  destinationsLoaded: false,
  loading: false,
  error: null,
};

export const mutations = {
  setDestinations(state, items) {
    state.destinations = Array.isArray(items) ? items : [];
    state.destinationsLoaded = true;
  },
  setLoading(state, v) {
    state.loading = !!v;
  },
  setError(state, e) {
    state.error = e || null;
  },
};

export const actions = {
  async fetchDestinations({ commit, state }, { force = false } = {}) {
    if (state.destinationsLoaded && !force) return state.destinations;
    commit("setLoading", true);
    commit("setError", null);
    try {
      // [SSO debug] open DevTools Console (Preserve log) to inspect these.
      // Cheap to keep on dev/UAT builds; trim later if logs ever leak PII.
      console.log("[sso/fetchDestinations] →", { force });
      const res = await axios.get(`/smegp/sso/destinations`);
      const items = res.data?.items || [];
      console.log("[sso/fetchDestinations] ←", {
        status: res.status,
        cached: res.data?.cached,
        items_count: items.length,
        items,
      });
      commit("setDestinations", items);
      return items;
    } catch (e) {
      console.error("[sso/fetchDestinations] error:", {
        status: e.response?.status,
        data: e.response?.data,
        message: e.message,
      });
      commit(
        "setError",
        e.response?.data?.message || e.message || "ไม่สามารถโหลดรายการบริการได้"
      );
      return [];
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Mint an SSO token and navigate to destination.
   * Returns true on success (browser will be redirected), false on error.
   *
   * `windowRef` — tab เปล่าที่ caller เปิดค้างไว้ตั้งแต่ตอนคลิก (กัน popup blocker).
   * ถ้าส่งมาจะพา tab นั้นไป redirectUrl แทนการเปลี่ยน tab ปัจจุบัน และถ้าพลาด
   * ระหว่างทางจะปิด tab เปล่าให้ ไม่ทิ้ง about:blank ค้างไว้. ไม่ส่งมา = เปิด tab เดิม.
   */
  async navigateTo(
    { commit },
    {
      destinationEServiceCode,
      destinationEServiceId,
      redirectPath,
      windowRef,
    } = {}
  ) {
    const closeBlankTab = () => {
      try {
        if (windowRef && !windowRef.closed) windowRef.close();
      } catch (err) {
        // ปิดไม่ได้ก็ไม่เป็นไร — ไม่ให้ error ตรงนี้กลบ error จริงที่กำลัง handle อยู่
      }
    };

    commit("setLoading", true);
    commit("setError", null);
    try {
      const body = {};
      if (destinationEServiceCode)
        body.destinationEServiceCode = destinationEServiceCode;
      if (destinationEServiceId)
        body.destinationEServiceId = destinationEServiceId;
      if (redirectPath) body.redirectPath = redirectPath;

      // [SSO debug] log the request body before posting.  These survive
      // the post-redirect Network-panel wipe IF "Preserve log" is on in
      // DevTools Console.
      console.log("[sso/navigateTo] →", body);

      const res = await axios.post(`/smegp/sso/generate-token`, body);

      // [SSO debug] log full response data + headers (status, redirectUrl,
      // expiresAt, transactionCode if present).  Read this in the Console
      // tab — Network tab's Response body is wiped by the navigation that
      // happens 1 line below.
      console.log("[sso/navigateTo] ←", {
        status: res.status,
        data: res.data,
        redirectUrl: res.data?.redirectUrl,
        expiresAt: res.data?.expiresAt,
      });

      const url = res.data?.redirectUrl;
      if (!url) {
        console.error(
          "[sso/navigateTo] no redirectUrl in response — bailing",
          res.data
        );
        commit("setError", "ไม่ได้รับ redirectUrl จากระบบ SSO");
        closeBlankTab();
        return false;
      }

      // windowRef ถูกเปิดไว้แล้วตอนคลิก — ถ้ามีและยังไม่ถูกปิด ให้พาไปที่ปลายทาง
      // ตัด opener ทิ้งก่อนกัน reverse tabnabbing (ตอนนี้ยังเป็น about:blank
      // จึงเซ็ตได้ ถ้ารอจน navigate แล้วจะกลายเป็น cross-origin แล้วโยน error)
      // ใช้ replace() เพื่อไม่ให้ about:blank ค้างใน history ของ tab ใหม่
      if (windowRef && !windowRef.closed) {
        console.log("[sso/navigateTo] navigating to (new tab):", url);
        try {
          windowRef.opener = null;
        } catch (err) {
          // เบราว์เซอร์บางตัวไม่ยอมให้เซ็ต — ข้ามไป ยังเปิดได้ปกติ
        }
        windowRef.location.replace(url);
      } else {
        // popup ถูกบล็อก หรือ caller ไม่ได้ส่ง windowRef มา → พฤติกรรมเดิม
        console.log("[sso/navigateTo] navigating to (same tab):", url);
        window.location.href = url;
      }
      return true;
    } catch (e) {
      console.error("[sso/navigateTo] error:", {
        status: e.response?.status,
        data: e.response?.data,
        message: e.message,
      });
      const code =
        e.response?.data?.name ||
        e.response?.data?.type ||
        e.response?.status;
      const upstreamMsg =
        e.response?.data?.message ||
        e.response?.data?.data?.message ||
        e.message;
      // Map known errorCodes to actionable Thai messages.
      let msg;
      switch (code) {
        case "smeOneId_not_available":
          msg =
            "ระบบไม่สามารถระบุข้อมูลผู้ใช้ของคุณได้ กรุณาลงทะเบียน SME ก่อนใช้งาน";
          break;
        case "smeOneId_not_registered_on_sso":
          msg =
            "ระบบ SME ONE ยังไม่รู้จักผู้ใช้นี้ — กรุณาติดต่อผู้ดูแลระบบเพื่อลงทะเบียนเพิ่ม";
          break;
        case "sso_disabled":
          msg = "ระบบ SSO ถูกปิดชั่วคราว กรุณาลองใหม่ภายหลัง";
          break;
        default:
          msg = upstreamMsg || "ไม่สามารถส่งไปยังบริการปลายทางได้";
      }
      commit("setError", { code, message: msg });
      closeBlankTab();
      return false;
    } finally {
      commit("setLoading", false);
    }
  },

  clearError({ commit }) {
    commit("setError", null);
  },
};

export const getters = {
  destinations(state) {
    return state.destinations;
  },
  hasDestinations(state) {
    return state.destinations.length > 0;
  },
  loading(state) {
    return state.loading;
  },
  error(state) {
    return state.error;
  },
};
